import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface PermissionWrapperProps {
  requiredPermissions: string[];
  userPermissions: string[];
  requireAll?: boolean;
  children: ReactNode;
  fallback?: ReactNode;
}

const DefaultFallback = ({
  permissions,
  currentLevel,
}: {
  permissions: string[];
  currentLevel: string;
}) => {
  const { t } = useTranslation();
  const requiredLevel = parseInt(permissions[0].split(" ")[2]);
  const userLevel = parseInt(currentLevel.split(" ")[2]);

  const getPermissionDescription = (level: number) => {
    switch (level) {
      case 0:
        return t("permissions.noAccess");
      case 1:
        return t("permissions.messagesOnly");
      case 2:
        return t("permissions.messagesAndStocks");
      case 3:
        return t("permissions.allFeatures");
      default:
        return t("permissions.unknownLevel");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-4 text-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          {t("permissions.accessDenied")}
        </h1>
        <p className="text-gray-600 mb-6">
          {t("permissions.noPermissionMessage")}
        </p>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">
              {t("permissions.currentAccessLevel")}
            </h2>
            <p className="text-gray-600">
              {getPermissionDescription(userLevel)}
            </p>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-2">
              {t("permissions.requiredAccessLevel")}
            </h2>
            <p className="text-gray-600">
              {getPermissionDescription(requiredLevel)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export function PermissionWrapper({
  requiredPermissions,
  userPermissions,
  requireAll = true,
  children,
  fallback,
}: PermissionWrapperProps) {
  const hasAllPermissions = (permissions: string[]) => {
    return permissions.every((permission) =>
      userPermissions.includes(permission),
    );
  };

  const hasAnyPermission = (permissions: string[]) => {
    return permissions.some((permission) =>
      userPermissions.includes(permission),
    );
  };

  const hasAccess = requireAll
    ? hasAllPermissions(requiredPermissions)
    : hasAnyPermission(requiredPermissions);

  if (!hasAccess) {
    return (
      fallback || (
        <DefaultFallback
          permissions={requiredPermissions}
          currentLevel={userPermissions[0]}
        />
      )
    );
  }

  return <>{children}</>;
}
